import {getTestProps} from '@benchling/js-test-utils';
import {
  Anchor,
  Box,
  Button,
  createStyles,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import React, {useCallback} from 'react';

import {assertAndContinue} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {RoutineExecutionKind, RoutineExecutionStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {agentConfigurationUrl} from 'llm/routes/UrlUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import fuzzydate from 'util/fuzzydate';
import {assertNever} from 'util/Types';

import {TriggerType} from './RoutineTypes';
import {AgentHomepageRoutineExecution} from './useAgentHomepageRoutineExecutions';

const AGENT_ACTIVITY_EMPTY_ILLUSTRATION_PATH = '/static/img/ai-agents/agent-activity-empty.png';

const useStyles = createStyles((theme) => ({
  row: {
    display: 'block',
    width: '100%',
    padding: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    textAlign: 'left',
  },
  clickableRow: {
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  icon: {
    color: theme.colors.gray[6],
    marginTop: 2,
  },
  warningIcon: {
    color: theme.colors.orange[7],
  },
  timestamp: {
    whiteSpace: 'nowrap',
  },
  // Lets the run name truncate instead of pushing the timestamp off the row.
  shrinkable: {
    minWidth: 0,
  },
  inlineMetadata: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
}));

const useEmptyStateStyles = createStyles((theme) => ({
  // The illustration starts 10vh below the activity content, which begins under the heading.
  illustratedEmptyState: {
    paddingTop: '10vh',
  },
  emptyIllustration: {
    display: 'block',
    width: '80px',
    height: 'auto',
    paddingBottom: theme.spacing.md,
  },
}));

function getFailureReason(status: RoutineExecutionStatus): string {
  switch (status) {
    case RoutineExecutionStatus.FAILED:
      return 'The run failed before it could be dispatched.';
    case RoutineExecutionStatus.RATE_LIMITED:
      return 'The run was rate limited.';
    case RoutineExecutionStatus.PENDING:
      return 'The run has not been dispatched yet.';
    default:
      return 'The run was not dispatched.';
  }
}

function getTriggerLabel(triggerType: TriggerType): string {
  return triggerType === TriggerType.EVENT_BASED ? 'Event based' : 'Time based';
}

function getRunKindLabel(
  executionKind: RoutineExecutionKind | null | undefined,
  triggerType: TriggerType
): string {
  switch (executionKind) {
    case RoutineExecutionKind.SCHEDULED:
      return 'Time-based trigger';
    case RoutineExecutionKind.EVENT:
      return 'Event-based trigger';
    case RoutineExecutionKind.MANUAL_RUN:
      return 'Run now';
    case RoutineExecutionKind.USER_MESSAGE:
      return 'Direct message';
    case RoutineExecutionKind.AGENT_MESSAGE:
      return 'Agent message';
    case null:
    case undefined:
      return getTriggerLabel(triggerType);
    default:
      assertAndContinue(false, `Unexpected routine execution kind: ${assertNever(executionKind)}`);
      return getTriggerLabel(triggerType);
  }
}

interface RunRowProps {
  run: AgentHomepageRoutineExecution;
  routineName: string;
  triggerType: TriggerType;
  inlineMetadata: boolean;
  metadataColor: string;
}

function RunRow({run, routineName, triggerType, inlineMetadata, metadataColor}: RunRowProps): JSX.Element {
  const {classes, cx} = useStyles();
  const chatSessionId = run.chatSession?.id ?? null;
  const wasDispatched = run.status === RoutineExecutionStatus.DISPATCHED;
  const canViewChat = wasDispatched && chatSessionId != null && APIUtil.isChatSession(chatSessionId);
  const {executionKind} = run;
  const dispatchedIcon =
    executionKind !== RoutineExecutionKind.USER_MESSAGE ? 'robot-outline' : 'comment-outline';

  const handleOpenChat = useCallback(() => {
    if (chatSessionId != null) {
      openChat(chatSessionId);
    }
  }, [chatSessionId]);

  const name = run.chatSession?.name ?? routineName;
  const metadata = getRunKindLabel(executionKind, triggerType);

  const content = (
    <Group position="apart" noWrap={true} align="flex-start" spacing="sm">
      <Group spacing="sm" noWrap={true} align="flex-start" className={classes.shrinkable}>
        {wasDispatched ? (
          <BenchlingIcon icon={dispatchedIcon} className={classes.icon} />
        ) : (
          <Tooltip label={getFailureReason(run.status)} multiline={true} withArrow={true}>
            <Box component="span" role="img" aria-label="Run warning">
              <BenchlingIcon icon="exclamation-triangle" className={cx(classes.icon, classes.warningIcon)} />
            </Box>
          </Tooltip>
        )}
        {inlineMetadata ? (
          <Group
            spacing="sm"
            noWrap={true}
            align="baseline"
            className={classes.shrinkable}
            {...getTestProps({component: 'RoutineRunsTab', element: 'inlineRunLabel'})}
          >
            <Text size="sm" fw={500} truncate={true} className={classes.shrinkable}>
              {name}
            </Text>
            <Text size="sm" c="gray.5" aria-hidden={true} className={classes.inlineMetadata}>
              •
            </Text>
            <Text size="sm" c={metadataColor} className={classes.inlineMetadata}>
              {metadata}
            </Text>
          </Group>
        ) : (
          <Stack spacing={0}>
            <Text size="sm" fw={500} lineClamp={1}>
              {name}
            </Text>
            <Text size="xs" c={metadataColor} lineClamp={1}>
              {metadata}
            </Text>
          </Stack>
        )}
      </Group>
      <Text size="xs" c="dimmed" className={classes.timestamp}>
        {fuzzydate(run.createdAt, {compact: true})}
      </Text>
    </Group>
  );

  if (canViewChat) {
    return (
      <UnstyledButton className={cx(classes.row, classes.clickableRow)} onClick={handleOpenChat}>
        {content}
      </UnstyledButton>
    );
  }
  return <Box className={classes.row}>{content}</Box>;
}

interface RoutineRunsListProps {
  runs: ReadonlyArray<AgentHomepageRoutineExecution>;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
  routineName: string;
  triggerType: TriggerType;
  /** Show the run-kind label on the name's line instead of below it. */
  inlineMetadata: boolean;
  /** Mantine color for the run-kind label. */
  metadataColor: string;
  /** Agent whose details the illustrated empty state's Settings link opens. */
  routineId: string;
  /** The illustrated empty state suggests starting a chat only when the viewer can start one. */
  canStartChat: boolean;
}

export function RoutineRunsList({
  runs,
  isLoading,
  isError,
  hasMore,
  loadMore,
  routineName,
  triggerType,
  inlineMetadata,
  metadataColor,
  routineId,
  canStartChat,
}: RoutineRunsListProps): JSX.Element {
  const {classes} = useEmptyStateStyles();
  if (isLoading && runs.length === 0) {
    return (
      <Group position="center" py="lg">
        <Loader size="sm" />
      </Group>
    );
  }

  if (isError && runs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Failed to load runs. Please try again.
      </Text>
    );
  }

  if (runs.length === 0) {
    const settingsUrl = agentConfigurationUrl(routineId);
    return (
      <Stack
        align="center"
        spacing="sm"
        className={classes.illustratedEmptyState}
        {...getTestProps({component: 'RoutineRunsTab', element: 'emptyIllustration'})}
      >
        <img
          src={staticURL(AGENT_ACTIVITY_EMPTY_ILLUSTRATION_PATH)}
          alt=""
          className={classes.emptyIllustration}
          {...getTestProps({component: 'RoutineRunsTab', element: 'emptyIllustrationImage'})}
        />
        <Stack align="center" spacing="xs">
          <Text size="lg" fw={600} ta="center">
            This agent hasn't run yet.
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            {canStartChat ? 'Start a chat or configure it in' : 'Configure it in'}{' '}
            <Anchor
              href={settingsUrl}
              size="sm"
              onClick={(event) => {
                benchlingUtil.navigate(event);
              }}
            >
              Settings
            </Anchor>
          </Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing="md">
      <Stack spacing={4}>
        {runs.map((run) => (
          <RunRow
            key={run.id}
            run={run}
            routineName={routineName}
            triggerType={triggerType}
            inlineMetadata={inlineMetadata}
            metadataColor={metadataColor}
          />
        ))}
      </Stack>
      {hasMore && (
        <Group position="center">
          <Button variant="ghost" size="sm" onClick={loadMore}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
